import { useState } from 'react';
import { Card, Button, StatCard, Icon } from '@getpay93/ui';
import { useMePlan, useMonthlyReport } from '../../data/hooks';
import { usePaywall } from '../../data/paywall';
import { reportToCsv, downloadCsv, reportToPdf } from '../../lib/exportReport';
import { money, shortDate } from '../../lib/format';

function currentYm(): { y: number; m: number } {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1 };
}

export function Reportes() {
  const plan = useMePlan();
  const { openUpgrade } = usePaywall();
  const [ym, setYm] = useState(currentYm);
  const isFree = plan.data ? plan.data.effectiveTier === 'free' : false;
  const report = useMonthlyReport(ym.y, ym.m, plan.isSuccess && !isFree);

  if (plan.isLoading) return <p style={{ color: 'var(--text-hint)' }}>Cargando…</p>;

  if (isFree) {
    return (
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: 24, gap: 10 }}>
          <Icon name="lock" size={32} color="var(--text-faint)" />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-strong)' }}>Reportes mensuales</h2>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>Disponible en el plan Pro. Mejora tu plan para ver reportes y exportar CSV/PDF.</p>
          <Button onClick={openUpgrade}>Mejorar plan</Button>
        </div>
      </Card>
    );
  }

  const r = report.data;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <input
          type="month"
          value={`${ym.y}-${String(ym.m).padStart(2, '0')}`}
          onChange={(e) => { const [y, m] = e.currentTarget.value.split('-'); if (y && m) setYm({ y: Number(y), m: Number(m) }); }}
          style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text-body)', fontFamily: 'var(--font-sans)' }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="outline" size="sm" iconLeft="download" disabled={!r} onClick={() => r && downloadCsv(`reporte-${r.label}.csv`, reportToCsv(r))}>CSV</Button>
          <Button variant="outline" size="sm" iconLeft="file-text" disabled={!r} onClick={() => r && reportToPdf(r)}>PDF</Button>
        </div>
      </div>

      {report.isLoading ? <p style={{ color: 'var(--text-hint)' }}>Cargando reporte…</p> : null}
      {r ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <StatCard label="Cobrado" value={money(r.totals.collected)} tone="green" icon="banknote" />
            <StatCard label="Pendiente" value={money(r.totals.pending)} tone="amber" icon="clock" />
            <StatCard label="Vencido" value={money(r.totals.overdue)} tone="red" icon="alert-triangle" />
            <StatCard label="Tasa de cobro" value={`${Math.round(r.totals.collectionRate * 100)}%`} tone="green" icon="trending-up" />
          </div>
          {r.topDebtors.length > 0 ? (
            <Card>
              <strong style={{ color: 'var(--text-strong)' }}>Top deudores</strong>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {r.topDebtors.map((d) => (
                  <div key={d.clientName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: 'var(--text-body)' }}>{d.clientName}</span>
                    <span style={{ color: 'var(--status-overdue)' }}>{money(d.overdueAmount)} ({d.overdueCount})</span>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
          <Card padding={0}>
            {r.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                <span style={{ color: 'var(--text-body)' }}>{l.clientName}</span>
                <span style={{ color: 'var(--text-muted)' }}>{money(l.amount)} · {shortDate(l.dueDate)} · {l.status}</span>
              </div>
            ))}
            {r.lines.length === 0 ? <p style={{ padding: 16, color: 'var(--text-hint)' }}>Sin movimientos este mes.</p> : null}
          </Card>
        </>
      ) : null}
    </div>
  );
}
